import Vue from 'vue';
import { WebAuth } from 'cidaas-javascript-sdk';
import { options } from '@/auth/index';
import { parseToken } from './tokenParsingService';
import Oidc from 'oidc-client-ts';
import { authenticationRepository } from '@/api';
import { DeaAdditionalUserData } from '@/api/interfaces/authentication/deaAdditionalUserData';

interface CidaasWindow extends Window {
    usermanager: Oidc.UserManager;
}

const cidaas = new WebAuth(options);

const storageKeys = {
    oidcUser: `user:${options.authority}:${options.client_id}`,
    returnUrl: 'dea.returnUrl',
    additionalUserData: 'dea.additionalUserData',
};

interface CidaasUserInfo {
    /* eslint-disable @typescript-eslint/naming-convention */
    access_token: string;
    id_token: string;
    refresh_token: string;
    token_type: string;
    profile: {
        auth_time: number;
        email: string;
        locale: string;
        name: string;
        roles: string[];
        sub: string;
    };
    expires_at: number;
    expired: boolean;
    expires_in: number;
    /* eslint-enable @typescript-eslint/naming-convention */
}

interface CidaasRenewedToken {
    /* eslint-disable @typescript-eslint/naming-convention */
    access_token: string;
    id_token: string;
    refresh_token: string;
    token_type: string;
    /* eslint-enable @typescript-eslint/naming-convention */
}

export type Role =
    | 'DEA Admin'
    | 'DEA Debug'
    | 'DEA Dummy'
    | 'DEA NonexistingTestRole';

export interface AuthService {
    readonly isAuthenticated: boolean;
    readonly token: string | null;
    readonly sub: string | null;
    readonly deaAdditionalUserData: DeaAdditionalUserData | null;
    readonly email: string | null;
    restore(): Promise<void>;
    authenticate(): Promise<void>;
    logout(callLogoutToken?: boolean): Promise<void>;
    handleLoginCallback(): Promise<void>;
    handleLogoutCallback(): Promise<void>;
    getMissingRoles(...requiredRoles: Role[]): Role[];
    hasRoles(...requiredRoles: Role[]): boolean;
}

class CidaasAuthService implements AuthService {
    private static readonly tokenRenewalBufferMs = 900000; // renew 15 minutes before expiry
    private userInfo: CidaasUserInfo | null = null;
    private additionalUserData: DeaAdditionalUserData | null = null;
    private previousTokenRenewalTime: number | null = null;
    private tokenRenewalPromise: Promise<CidaasRenewedToken> | null = null;
    private fetchAdditionalUserDataPromise: Promise<void> | null = null;

    public async restore(): Promise<void> {
        this.userInfo = await this.getUserInfo();
        this.restoreAdditionalUserData();
        if (!this.additionalUserData) {
            await this.fetchAdditionalUserData();
        }
    }

    private restoreAdditionalUserData(): void {
        const stored = localStorage.getItem(storageKeys.additionalUserData);
        if (stored) {
            this.additionalUserData = JSON.parse(
                stored,
            ) as DeaAdditionalUserData;
        }
    }

    public get isAuthenticated(): boolean {
        return !!this.userInfo && !this.userInfo.expired;
    }

    public get token(): string | null {
        return this.isAuthenticated && this.userInfo
            ? this.userInfo.access_token
            : null;
    }

    public get sub(): string | null {
        return this.isAuthenticated && this.userInfo
            ? this.userInfo.profile.sub
            : null;
    }

    public get deaAdditionalUserData(): DeaAdditionalUserData | null {
        return this.additionalUserData;
    }

    private fetchAdditionalUserData(): Promise<void> {
        if (!this.isAuthenticated) {
            this.additionalUserData = null;
            return Promise.resolve();
        }

        if (this.fetchAdditionalUserDataPromise) {
            return this.fetchAdditionalUserDataPromise;
        }

        this.fetchAdditionalUserDataPromise = authenticationRepository
            .getAdditionalUserData()
            .then((data) => {
                localStorage.setItem(
                    storageKeys.additionalUserData,
                    JSON.stringify(data),
                );
                this.additionalUserData = data;
            })
            .finally(() => {
                this.fetchAdditionalUserDataPromise = null;
            });

        return this.fetchAdditionalUserDataPromise;
    }

    public get email(): string | null {
        return this.isAuthenticated && this.userInfo
            ? this.userInfo.profile.email
            : null;
    }

    private getUserInfo(): Promise<CidaasUserInfo> {
        return cidaas.getUserInfo();
    }

    // the user of the OIDC user manager is not updated automatically when
    // renewing the token, so we have to set the renewed token by ourselves
    private async syncOidcUser(data: CidaasRenewedToken): Promise<void> {
        const storedUserInfoString = await options.userStore?.get(
            storageKeys.oidcUser,
        );
        const storedUserInfo = storedUserInfoString
            ? (JSON.parse(storedUserInfoString) as CidaasUserInfo)
            : null;

        if (!storedUserInfo) {
            throw new Error('could not read user from session storage');
        }

        storedUserInfo.access_token = data.access_token;
        storedUserInfo.id_token = data.id_token;
        storedUserInfo.refresh_token = data.refresh_token;
        storedUserInfo.token_type = data.token_type;
        storedUserInfo.expires_at = parseToken<{ exp: number }>(
            data.access_token,
        ).exp;

        // store updated user and refresh user info
        await options.userStore?.set(
            storageKeys.oidcUser,
            JSON.stringify(storedUserInfo),
        );
    }

    public async renewToken(): Promise<void> {
        if (!this.userInfo) {
            throw new Error('not authenticated');
        }

        // always get the current user info in case it was updated in other tabs
        this.userInfo = await this.getUserInfo();

        // only renew if token is close to expiring
        const currentTime = new Date().getTime();
        const expiresAtMs = this.userInfo.expires_at * 1000;
        const timeUntilExpiryMs = expiresAtMs - currentTime;

        if (timeUntilExpiryMs > CidaasAuthService.tokenRenewalBufferMs) {
            return; // token is still valid, no need to renew yet
        }

        // await any open token renewal promises to avoid sending outdated refresh tokens
        const refreshToken = this.tokenRenewalPromise
            ? (await this.tokenRenewalPromise).refresh_token
            : this.userInfo.refresh_token;

        this.tokenRenewalPromise = cidaas.renewToken({
            /* eslint-disable @typescript-eslint/naming-convention */
            refresh_token: refreshToken,
            user_agent: navigator.userAgent,
            ip_address: '',
            accept_language: navigator.language,
            lat: '',
            lng: '',
            finger_print: '',
            referrer: location.href,
            pre_login_id: '',
            login_type: '',
            device_code: '',
            /* eslint-enable @typescript-eslint/naming-convention */
        }) as Promise<CidaasRenewedToken>;

        const renewedToken = await this.tokenRenewalPromise;
        if (!renewedToken || !renewedToken.access_token) {
            throw new Error('could not renew token');
        }

        await this.syncOidcUser(renewedToken);
        this.userInfo = await this.getUserInfo();
        this.tokenRenewalPromise = null;
        await this.fetchAdditionalUserData();
    }

    public async authenticate(): Promise<void> {
        if (this.isAuthenticated) {
            await this.renewToken();
        } else {
            await this.login();
        }
    }

    private async login(): Promise<void> {
        // store return URL in session
        sessionStorage.setItem(storageKeys.returnUrl, window.location.href);
        this.clearStaleState();

        // forward to cidaas login page
        await cidaas.loginWithBrowser();
    }

    public async logout(callLogoutToken = true): Promise<void> {
        this.additionalUserData = null;
        localStorage.removeItem(storageKeys.additionalUserData);

        if (!this.userInfo) {
            return;
        }

        if (callLogoutToken) {
            await authenticationRepository.logoutToken();
        }

        await cidaas.logout();
    }

    public async handleLoginCallback(): Promise<void> {
        await cidaas.loginCallback();

        // forward to return URL and release it from storage
        const returnUrl = sessionStorage.getItem(storageKeys.returnUrl);
        sessionStorage.removeItem(storageKeys.returnUrl);

        if (returnUrl) {
            window.location.href = returnUrl;
        }
    }

    public async handleLogoutCallback(): Promise<void> {
        await cidaas.logoutCallback();
        window.location.href = '/';
    }

    public getMissingRoles(...requiredRoles: Role[]): Role[] {
        if (!this.userInfo) {
            throw new Error('not authenticated');
        }

        const userRoles = this.userInfo.profile.roles;

        return requiredRoles.filter(
            (requiredRole) => !userRoles.includes(requiredRole),
        );
    }

    public hasRoles(...requiredRoles: Role[]): boolean {
        return this.getMissingRoles(...requiredRoles).length === 0;
    }

    private clearStaleState() {
        // Everytime logout is triggered we get two entries in local storage (oidc.<32-digit-id>) (one for logout, one for signin) but only one is cleaned up after redirect back to dea page (pretty sure signin entry gets removed).
        // This abandoned (logout) entry can indicate an oidc workflow which was not completed.
        // Since I cannot find any wrong implementation lets clear stale states (left-over entries) here.

        // window.usermanager is set by cidaas
        // clearStaleState is using staleStateAge which is set by oidc config
        (window as CidaasWindow).usermanager?.clearStaleState();
    }
}

export const authService = Vue.observable<AuthService>(new CidaasAuthService());
